/**
 * Client-side port of the backend's `validateCodeChecksum` from
 * `services/couponService.js`. SAME alphabet, SAME doubling rule, SAME
 * checksum position. Drift between this file and the backend produces
 * a brutal class of "valid card rejected" bugs — keep these in sync.
 *
 * Why duplicate it client-side? Because the convention floor wifi will
 * be terrible. Catching the 20% of typos that a Luhn-style check would
 * catch (single-digit + adjacent transpose) without a round-trip is the
 * single biggest UX win we can ship.
 */

/**
 * Base32 alphabet with ambiguous characters removed. Matches
 * couponService.js#CODE_ALPHABET exactly. 22 letters + 4 digits = 26.
 */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ2345';
const CODE_PREFIX = 'LXIA';
const CODE_RANDOM_LENGTH = 11;
const PAYLOAD_LENGTH = CODE_RANDOM_LENGTH + 1; // 12

export const COUPON_CODE_ALPHABET = CODE_ALPHABET;
export const COUPON_CODE_PREFIX = CODE_PREFIX;

/**
 * Reason codes mirror the backend's `validateCodeChecksum` return
 * shape so a future API alignment ("show the user the precise
 * reason") doesn't require a second mapping table.
 */
export type ChecksumFailureReason =
  | 'NOT_A_STRING'
  | 'PREFIX_MISMATCH'
  | 'LENGTH_MISMATCH'
  | 'CHARSET_MISMATCH'
  | 'CHECKSUM_MISMATCH';

export interface ChecksumResult {
  valid: boolean;
  /** Canonical LXIA-XXXX-XXXX-XXXX form when valid. */
  normalized?: string;
  reason?: ChecksumFailureReason;
}

/**
 * Compute the Luhn-style checksum char over the random body.
 *
 * Direct port of computeChecksumChar — same alphabet, same per-position
 * doubling (every char at odd-from-the-right index gets *2 with
 * subtract-and-add wrap). The output char is the single ALPHABET[v]
 * that brings the total mod N to 0.
 */
function computeChecksumChar(body: string): string {
  const N = CODE_ALPHABET.length;
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const ch = body[body.length - 1 - i];
    let v = CODE_ALPHABET.indexOf(ch);
    // defensive — validateCodeChecksum filters non-alphabet chars before we get here
    /* v8 ignore start */
    if (v < 0) v = 0;
    /* v8 ignore stop */
    if (i % 2 === 0) {
      v = v * 2;
      if (v >= N) v = (v % N) + Math.floor(v / N);
    }
    sum += v;
  }
  const checksumValue = (N - (sum % N)) % N;
  return CODE_ALPHABET[checksumValue];
}

/**
 * Validate a code string. Accepts the code in any case, with or
 * without dashes / whitespace — the user could have typed it however.
 * Returns the canonical LXIA-XXXX-XXXX-XXXX form when valid.
 */
export function validateCodeChecksum(code: unknown): ChecksumResult {
  if (typeof code !== 'string' || code.length === 0) {
    return { valid: false, reason: 'NOT_A_STRING' };
  }
  const stripped = code.replace(/[\s-]/g, '').toUpperCase();
  if (!stripped.startsWith(CODE_PREFIX)) {
    return { valid: false, reason: 'PREFIX_MISMATCH' };
  }
  const payload = stripped.slice(CODE_PREFIX.length);
  if (payload.length !== PAYLOAD_LENGTH) {
    return { valid: false, reason: 'LENGTH_MISMATCH' };
  }
  for (const ch of payload) {
    if (!CODE_ALPHABET.includes(ch)) {
      return { valid: false, reason: 'CHARSET_MISMATCH' };
    }
  }
  const body = payload.slice(0, CODE_RANDOM_LENGTH);
  const expected = computeChecksumChar(body);
  const actual = payload[CODE_RANDOM_LENGTH];
  if (expected !== actual) {
    return { valid: false, reason: 'CHECKSUM_MISMATCH' };
  }
  const normalized = `${CODE_PREFIX}-${payload.slice(0, 4)}-${payload.slice(4, 8)}-${payload.slice(8, 12)}`;
  return { valid: true, normalized };
}

/**
 * Normalize a user-entered string to the canonical dash form WITHOUT
 * checking the checksum. Useful for the input field's live formatter:
 * the user types "lxia7k2m", we display "LXIA-7K2M-…". We deliberately
 * keep partial inputs editable (don't force length on every keystroke).
 *
 * - Uppercases
 * - Strips whitespace + dashes
 * - Re-inserts dashes after positions 4, 8, 12 (LXIA + three groups)
 * - Caps total length at LXIA + 12 chars
 */
export function formatCodeForInput(raw: string): string {
  const stripped = (raw || '').replace(/[\s-]/g, '').toUpperCase();
  // Cap to prefix(4) + payload(12) = 16 chars max.
  const capped = stripped.slice(0, 16);
  if (capped.length <= 4) return capped;
  const groups: string[] = [capped.slice(0, 4)];
  // redundant with the line-116 guard above (capped.length is always > 4 here);
  // kept for symmetry with the other length checks below
  /* v8 ignore start */
  if (capped.length > 4) groups.push(capped.slice(4, 8));
  /* v8 ignore stop */
  if (capped.length > 8) groups.push(capped.slice(8, 12));
  if (capped.length > 12) groups.push(capped.slice(12, 16));
  return groups.join('-');
}
